/* eslint-disable @eslint-react/hooks-extra/no-direct-set-state-in-use-layout-effect */
import type { AnimationPlaybackControls } from "motion";
import { animate } from "motion";
import { useLayoutEffect, useRef, useState } from "react";
import { useIsMounted } from "../hooks/useIsMounted";
import { cn } from "../lib/utils";

/** The duration of the animation of each word */
const ANIMATION_DURATION = 500;

/** The delay between each word */
const STAGGER_DELAY = 75;

/** The delay at the start of a paragraph */
const PARAGRAPH_DELAY = 200;

export interface AppearingTextProps extends React.HTMLAttributes<HTMLDivElement> {
  html: string;
  withoutAnimation?: boolean;
  onWordAppearing?: () => void;
  onAnimationEnd?: () => void;
}

export function AppearingText({
  html,
  withoutAnimation,
  onWordAppearing,
  onAnimationEnd,
  ...props
}: AppearingTextProps) {
  const isMounted = useIsMounted();
  const parentRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  // The callbacks are stored in a ref so they don't retrigger the animation when they change
  const callbacks = useRef({ onAnimationEnd, onWordAppearing });
  callbacks.current = { onAnimationEnd, onWordAppearing };

  useLayoutEffect(() => {
    const parent = parentRef.current;
    const screen = document.querySelector<HTMLDivElement>(".screen");
    const words = new Set(parent?.querySelectorAll<HTMLElement>(".word"));

    if (!parent || !words?.size) return;

    if (withoutAnimation) {
      setIsReady(true);
      return;
    }

    // Animate each word with a staggered delay
    const animations = new Set<AnimationPlaybackControls>();
    let lastAnimation: AnimationPlaybackControls | null = null;
    let delay = 0;

    for (const word of words) {
      // Add extra delay to special words, like the first word of a paragraph
      // Don't stagger the first word of a paragraph
      if (delay > 0 && word.matches("p > :first-child, li")) {
        delay += PARAGRAPH_DELAY;
      }

      delay += STAGGER_DELAY;

      // Remove the underline until the last word of the underlined string appears
      if (word.matches(".underline > .word") && word.parentElement) {
        word.parentElement.style.textDecoration = "none";
      }

      lastAnimation = animate(
        word,
        {
          opacity: [0, 1],
          transform: ["translateY(10%)", "translateY(0%)"],
        },
        {
          duration: ANIMATION_DURATION / 1000,
          ease: [0.68, -0.6, 0.32, 1.6],
          delay: delay / 1000,
        },
      );

      // Call the onWordAppearing callback when the animation is finished
      lastAnimation.finished.then(() => {
        if (isMounted()) {
          callbacks.current.onWordAppearing?.();
        }
      });

      animations.add(lastAnimation);
    }

    setIsReady(true);

    // Call the onAnimationEnd callback when the last animation is finished
    lastAnimation?.finished.then(() => {
      if (isMounted()) {
        callbacks.current.onAnimationEnd?.();
      }
    });

    // Increase the speed of the animation when the user holds down the pointer on the trigger
    // element
    const speedUpTrigger = screen ?? parent;

    const pointerDown = () => {
      animations.forEach((animation) => (animation.speed = 3));
    };

    const pointerUp = () => {
      animations.forEach((animation) => (animation.speed = 1));
    };

    speedUpTrigger.addEventListener("pointerup", pointerUp);
    speedUpTrigger.addEventListener("pointercancel", pointerUp);
    speedUpTrigger.addEventListener("pointerdown", pointerDown);

    return () => {
      animations.forEach((animation) => animation.cancel());

      speedUpTrigger.removeEventListener("pointerup", pointerUp);
      speedUpTrigger.removeEventListener("pointercancel", pointerUp);
      speedUpTrigger.removeEventListener("pointerdown", pointerDown);
    };
  }, [isMounted, withoutAnimation, html]);

  return (
    // The html is coming from a trusted source
    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
    <div
      {...props}
      ref={parentRef}
      className={cn(
        "group max-w-prose space-y-4 text-lg font-medium",
        !isReady && "invisible",
        props.className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
